import { useEffect, useState } from "react";
import {
  Table,
  ScrollArea,
  Card,
  LoadingOverlay,
  createStyles,
  Text,
  Group,
} from "@mantine/core";
import { useQuery, useMutation } from "@apollo/client";
import { customLoader } from "components/utilities/loader";
import { GET_PRODUCT, GET_PRODUCT_SKUS } from "apollo/queries";
import { useViewportSize } from "@mantine/hooks";
import { Box } from "@mui/material";
import { showNotification } from "@mantine/notifications";
import ProductDiscount from "./productDiscount";
const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== "string") return "-";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};
const useStyles = createStyles((theme) => ({
  diff: {
    lineHeight: 1,
    fontWeight: 500,
    display: "flex",
  },
}));

function ProductDetailModal({ Id }) {
  const { classes } = useStyles();
  const [product, setProduct] = useState(null);
  const [productSku, setProductSku] = useState([]);

  const { height } = useViewportSize();

  const { loading: productLoading, refetch } = useQuery(GET_PRODUCT, {
    variables: { id: Id },
    onCompleted(data) {
      setProduct(data.product);
    },
    onError(err) {
      showNotification({
        color: "red",
        title: "Error",
        message: err?.graphQLErrors?.[0]?.message || "Failed to get product",
      });
    },
  });

  const { loading: productSkuLoading, refetch: skuFetch } = useQuery(
    GET_PRODUCT_SKUS,
    {
      variables: { product_id: Id },
      onCompleted(data) {
        console.log(data);
        setProductSku(data.productSkus || []);
      },
      onError(err) {
        showNotification({
          color: "red",
          title: "Error",
          message: err?.graphQLErrors?.[0]?.message || "Failed to get skus",
        });
      },
    }
  );
  useEffect(() => {
    refetch();
    skuFetch();
  }, []);
  const allAttributeNames = Array.from(
    new Set(
      productSku?.flatMap((sku) =>
        sku.variants.map((variant) => variant.attribute.name)
      )
    )
  );

  return (
    <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
      <div style={{ width: "98%", margin: "auto" }}>
        <LoadingOverlay
          visible={productLoading || productSkuLoading}
          color="blue"
          overlayBlur={2}
          loader={customLoader}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            flexDirection: "row",
            p: 1,
            m: 4,
            bgcolor: "background.paper",
            borderRadius: 1,
            gap: 10,
          }}
        >
          <Card
            style={{ flex: 0.6, marginRight: "30px", minHeight: "300px" }}
            shadow="sm"
            radius="md"
            withBorder
          >
            <div style={{ paddingLeft: "20px" }}>
              <Group align="flex-end" spacing="xs" mt={25}>
                <Text size="sm" weight={500} className={classes.diff}>
                  Product Name<span style={{ marginLeft: "5px" }}>:</span>
                </Text>
                <Text className={classes.value}>{product?.name}</Text>
              </Group>

              <Group align="flex-end" spacing="xs" mt={25}>
                <Text size="sm" weight={500} className={classes.diff}>
                  Category<span style={{ marginLeft: "5px" }}>:</span>
                </Text>
                <Text className={classes.value}>{product?.category?.name}</Text>
              </Group>

              <Group align="flex-end" spacing="xs" mt={25}>
                <Text size="sm" weight={500} className={classes.diff}>
                  Order Count<span style={{ marginLeft: "5px" }}>:</span>
                </Text>
                <Text className={classes.value}>{product?.orderCount}</Text>
              </Group>

              <Group align="flex-end" spacing="xs" mt={25}>
                <Text size="sm" weight={500} className={classes.diff}>
                  Product Skus<span style={{ marginLeft: "5px" }}>:</span>
                </Text>
                <Text className={classes.value}>{product?.skus.length}</Text>
              </Group>
            </div>
          </Card>

          <Card
            style={{ flex: 0.8, minHeight: "300px" }}
            shadow="sm"
            radius="md"
            withBorder
          >
            <div style={{ paddingLeft: "20px" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: 25,
                }}
              >
                <Text size="sm" weight={500} style={{ marginBottom: 4 }}>
                  Description<span style={{ marginLeft: 5 }}>:</span>
                </Text>
                <Text>{product?.description}</Text>
              </div>

              <Group align="flex-end" spacing="xs" mt={25}>
                <Text size="sm" weight={500} className={classes.diff}>
                  Short Description<span style={{ marginLeft: "5px" }}>:</span>
                </Text>
                <Text className={classes.value}>
                  {product?.short_description}
                </Text>
              </Group>
            </div>
          </Card>
        </Box>
        <Card style={{ marginTop: "30px" }} shadow="sm" p="lg">
          <ProductDiscount product={Id}/>
          <ScrollArea>
            <Text size="md" style={{marginTop:"10px"}} weight={500} className={classes.diff}>
              {product?.product_type === "SIMPLE_PRODUCT"
                ? "Price"
                : "Attributes and Values"}
            </Text>
            <Table withBorder withColumnBorders highlightOnHover>
              <thead>
                <tr>
                  {allAttributeNames.map((attrName) => (
                    <th key={attrName}> {capitalizeFirstLetter(attrName)}</th>
                  ))}
                  <th>Buy Price</th>
                  <th>Sell Price</th>
                </tr>
              </thead>
              <tbody>
                {productSku.length > 0 ? (
                  productSku.map((sku) => {
                    const variantMap = {};
                    sku.variants.forEach((v) => {
                      variantMap[v.attribute.name] = v.value;
                    });
                    return (
                      <tr key={sku.id}>
                        {allAttributeNames.map((attrName) => (
                          <td key={attrName}>
                            {" "}
                            {capitalizeFirstLetter(variantMap[attrName] || "-")}
                            {}
                          </td>
                        ))}
                        <td>{sku.buy_price}</td>
                        <td>{sku.price}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={allAttributeNames.length + 3}>
                      <Text>No SKUs available</Text>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ScrollArea>
        </Card>
      </div>
    </ScrollArea>
  );
}

export default ProductDetailModal;
